"use client";

import type { ProgressFieldInfo } from "@health/ui";
import { ProgressBar as ProgressBarUI, SkeletonLine } from "@health/ui";
import { useEffect, useRef } from "react";

type Input = {
  completed: number;
  total: number;
  current_section: string;
  fields?: ProgressFieldInfo[];
};

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
};

const MIN_FIELDS_TO_SHOW = 5;

export function ProgressBar({ input, onSubmit, submitted }: Props) {
  const { completed, total, current_section, fields } = input as unknown as Input;
  const didSubmit = useRef(false);

  useEffect(() => {
    if (!submitted && !didSubmit.current) {
      didSubmit.current = true;
      onSubmit({ acknowledged: true });
    }
  }, [submitted, onSubmit]);

  if (completed < MIN_FIELDS_TO_SHOW) return null;

  return (
    <ProgressBarUI
      completed={completed}
      total={total}
      sectionLabel={current_section}
      fields={fields}
    />
  );
}

/**
 * Loading skeleton mirroring ProgressBar — outer card with a section label
 * row (label + counter) and a slim progress track underneath.
 */
export function ProgressBarSkeleton() {
  return (
    <div className="bg-widget-bg rounded-2xl p-4 shadow-sm w-full space-y-2">
      <div className="flex items-center justify-between">
        <SkeletonLine $size="caption" $width="sm" />
        <SkeletonLine $size="caption" $width="xs" />
      </div>
      <SkeletonLine $size="caption" $width="full" className="h-2 rounded-full" />
    </div>
  );
}
